import Spinner from "components/Spinner";
import { useRef, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import authHeader from "services/auth-header";
import API from "services/axios";
import * as xlsx from "xlsx";

function ImportWOProductsExcel({ selectedWO, refreshFunc, showModal }) {
  /*   ALl States
   ********************************************* */
  const [woProductsData, setWoProductsData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const inputRef = useRef();

  const [productTypesList, setProductTypesList] = useState([]);
  const [showInvalidModal, setShowInvalidModal] = useState(false);
  const [parsedRows, setParsedRows] = useState(null);
  const [invalidTypes, setInvalidTypes] = useState([]); // array of strings
  const [replacements, setReplacements] = useState({}); // { invalidValue: chosenReplacement }
  const [loadingProductTypes, setLoadingProductTypes] = useState(false);

  // add small state to show filename
  const [selectedFileName, setSelectedFileName] = useState("");

  const [importErrors, setImportErrors] = useState([]);

  const mapRowsToPayload = (rows) =>
    rows.map(({ __originalProductType, __suggestion, ...rest }) => ({
      ...rest,
      WOId: selectedWO?.id,
      userId: selectedWO?.userId,
      companyId: selectedWO?.companyId,
    }));

  /*   All functions
   ********************************************* */
  const handleFileImport = (e) => {
    const file = e.target.files[0];
    const reader = new FileReader();

    reader.readAsArrayBuffer(file);
    reader.onload = (e) => {
      const data = e.target.result;
      const workbook = xlsx.read(data, { type: "array" });
      let jsonData = xlsx.utils.sheet_to_json(
        workbook.Sheets["wo_products"] ||
          workbook.Sheets[workbook.SheetNames[0]]
      );

      // Check for missing productType or productStyle
      const errors = [];
      jsonData.forEach((row, idx) => {
        const productType = row.productType
          ? String(row.productType).trim()
          : "";
        const productStyle = row.productStyle
          ? String(row.productStyle).trim()
          : "";
        if (productType === "" || productStyle === "") {
          errors.push(
            `Row ${idx + 2}: Missing required productType or productStyle.`
          );
        }
      });

      // Filter out rows missing productType or productStyle (after trimming)
      jsonData = jsonData
        .filter((row) => {
          const productType = row.productType
            ? String(row.productType).trim()
            : "";
          const productStyle = row.productStyle
            ? String(row.productStyle).trim()
            : "";
          return productType !== "" && productStyle !== "";
        })
        .map((row) => ({
          ...row,
          productType: row.productType ? String(row.productType).trim() : "",
          productStyle: row.productStyle ? String(row.productStyle).trim() : "",
        }));

      setImportErrors(errors);
      setSelectedFileName(file?.name || "");

      // Fetch product types from API to validate productType column
      (async () => {
        try {
          // Use provided pattern: set loading, call API, setProductTypesList(data), finally unset loading
          setLoadingProductTypes(true);
          const { data } = await API.get("/productTypes", {
            headers: authHeader(),
          });
          const apiTypes = Array.isArray(data) ? data : [];
          setProductTypesList(apiTypes);

          const normalize = (value) => (value ? String(value).trim() : "");
          const lower = (value) => normalize(value).toLowerCase();

          const findExact = (raw) =>
            apiTypes.find(
              (pt) => normalize(pt.productTypes) === normalize(raw)
            );

          const findCaseInsensitive = (raw) => {
            const target = lower(raw);
            return apiTypes.find((pt) => lower(pt.productTypes) === target);
          };

          const findSuggestion = (raw) => {
            const target = lower(raw);
            return (
              apiTypes.find(
                (pt) =>
                  pt.productTypes && lower(pt.productTypes).includes(target)
              ) ||
              apiTypes.find(
                (pt) =>
                  pt.productTypes && lower(pt.productTypes).startsWith(target)
              ) ||
              null
            );
          };

          const mappedRows = jsonData.map((row) => {
            const rawType = normalize(row.productType);
            const matched = findExact(rawType) || findCaseInsensitive(rawType);
            const suggestion =
              matched || !rawType ? null : findSuggestion(rawType);

            return {
              ...row,
              __originalProductType: rawType,
              productType: matched ? matched.productTypes : rawType,
              productTypeId: matched ? matched.productTypeId : null,
              __suggestion: suggestion,
            };
          });

          const invalidEntries = mappedRows.filter((r) => !r.productTypeId);
          const invalidKeys = [
            ...new Set(
              invalidEntries
                .map((r) => r.__originalProductType || r.productType)
                .filter(Boolean)
            ),
          ];

          if (invalidKeys.length > 0) {
            setParsedRows(mappedRows);
            setInvalidTypes(invalidKeys);

            const initReplacements = {};
            invalidKeys.forEach((key) => {
              const suggested = invalidEntries.find(
                (r) => (r.__originalProductType || r.productType) === key
              )?.__suggestion;
              initReplacements[key] = suggested
                ? String(suggested.productTypeId)
                : "";
            });
            setReplacements(initReplacements);
            setShowInvalidModal(true);
          } else {
            setParsedRows(null);
            setInvalidTypes([]);
            setReplacements({});
            setWoProductsData(mapRowsToPayload(mappedRows));
          }
        } catch (err) {
          alert(err.message);
        } finally {
          setLoadingProductTypes(false);
        }
      })();
    };
  };

  const applyReplacements = () => {
    for (const inv of invalidTypes) {
      if (!replacements[inv] || replacements[inv] === "") {
        return alert(`Please select a replacement for "${inv}"`);
      }
    }

    const finalRows = parsedRows.map((row) => {
      if (row.productTypeId) {
        return row;
      }

      const key = row.__originalProductType || row.productType;
      const chosenId = replacements[key];
      const matched = productTypesList.find(
        (pt) => String(pt.productTypeId) === String(chosenId)
      );

      if (!matched) {
        return row;
      }

      return {
        ...row,
        productType: matched.productTypes,
        productTypeId: matched.productTypeId,
        __suggestion: null,
      };
    });

    setWoProductsData(mapRowsToPayload(finalRows));
    setShowInvalidModal(false);
    setParsedRows(null);
    setInvalidTypes([]);
    setReplacements({});
    if (inputRef.current) {
      inputRef.current.value = null;
    }
  };

  const cancelInvalidModal = () => {
    // clear parsed data and close modal
    setParsedRows(null);
    setInvalidTypes([]);
    setReplacements({});
    setShowInvalidModal(false);
    if (inputRef.current) {
      inputRef.current.value = null;
    }
  };

  const addProductsFromFile = async () => {
    setIsLoading(true);

    try {
      let { data } = await API.post(
        "/wo-products/bulk-insert",
        woProductsData,
        {
          headers: authHeader(),
        }
      );
      if (data?.type === "success") {
        await refreshFunc();
        showModal(false);
        return;
      }
      console.log(data);
      return;
    } catch (err) {
      setIsLoading(false);
      alert(err.message);
    }
  };

  // improved clear function to reset all related states
  const clearAll = () => {
    if (inputRef.current) {
      inputRef.current.value = null;
    }
    setWoProductsData(null);
    setParsedRows(null);
    setInvalidTypes([]);
    setReplacements({});
    setProductTypesList([]);
    setSelectedFileName("");
  };

  // helper preview rows
  const previewRows = parsedRows
    ? parsedRows.slice(0, 3)
    : woProductsData
    ? woProductsData.slice(0, 3)
    : [];

  return (
    <div className="bg-white px-3 py-2 custom-border-radius shadow-lg">
      <div className="">
        <h4 className="">IMPORT DATA FILE</h4>
      </div>
      <hr className="mb-3 mt-0" />
      {isLoading && <Spinner />}
      <div className="d-flex align-items-start gap-2">
        <input
          ref={inputRef}
          type="file"
          className="form-control"
          onChange={handleFileImport}
        />
        <div className="d-flex flex-column">
          <button
            onClick={clearAll}
            className="btn btn-danger ms-1"
            title="Clear selected file and reset"
          >
            CLEAR
          </button>
        </div>
      </div>

      {/* File status & preview */}
      <div className="mt-3">
        {selectedFileName ? (
          <div className="d-flex align-items-center gap-2">
            <strong>File:</strong>
            <span className="text-truncate" style={{ maxWidth: 360 }}>
              {selectedFileName}
            </span>

            {loadingProductTypes ? (
              <span className="ms-2">
                <Spinner />
              </span>
            ) : invalidTypes && invalidTypes.length > 0 ? (
              <span className="badge bg-warning text-dark ms-2">
                Needs resolution ({invalidTypes.length} invalid type
                {invalidTypes.length > 1 ? "s" : ""})
              </span>
            ) : woProductsData ? (
              <span className="badge bg-success ms-2">Ready to import</span>
            ) : parsedRows ? (
              <span className="badge bg-info ms-2">
                Parsed ({parsedRows.length})
              </span>
            ) : null}
          </div>
        ) : null}

        {/* counts */}
        {parsedRows && !woProductsData && invalidTypes.length === 0 && (
          <div className="mt-2">
            <small>Parsed rows: {parsedRows.length}</small>
          </div>
        )}

        {woProductsData && (
          <div className="mt-2">
            <small>Import rows: {woProductsData.length}</small>
          </div>
        )}

        {/* preview table */}
        {previewRows && previewRows.length > 0 && (
          <div
            className="mt-3 table-responsive"
            style={{ maxHeight: 140, overflow: "auto" }}
          >
            <table className="table table-sm table-striped mb-0">
              <thead>
                <tr>
                  {/* try to show a few likely columns */}
                  <th>productType</th>
                  <th>productStyle</th>
                  <th>productColor</th>
                </tr>
              </thead>
              <tbody>
                {previewRows.map((r, idx) => (
                  <tr key={idx}>
                    <td>{r.productType ?? r.__originalProductType ?? "-"}</td>
                    <td>{r.productStyle ?? "-"}</td>
                    <td>{r.productColor ?? "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="text-muted small mt-1">
              Showing first {previewRows.length} rows
            </div>
          </div>
        )}

        {/* Error messages for import issues */}
        {importErrors.length > 0 && (
          <div className="alert alert-danger mt-2">
            <strong>File Errors:</strong>
            <ul className="mb-0">
              {importErrors.map((err, idx) => (
                <li key={idx}>{err}</li>
              ))}
            </ul>
            <div className="mt-1">
              Please fix these errors in your file before importing.
            </div>
          </div>
        )}
      </div>

      {/*   Buttons
       ********************************************* */}
      <div className="mt-3 flex-grow-1 d-flex justify-content-end align-items-end">
        <div>
          <button
            onClick={() => {
              showModal(false);
            }}
            type="button"
            className="btn btn-secondary me-2 px-1"
          >
            CLOSE
          </button>
          <button
            disabled={!woProductsData || importErrors.length > 0}
            onClick={addProductsFromFile}
            className="btn btn-primary"
          >
            {woProductsData ? `ADD (${woProductsData.length})` : "ADD"}
          </button>
        </div>
      </div>

      {/* Modal to resolve invalid product types */}
      <Modal
        contentClassName="custom-shadow-sm"
        show={showInvalidModal}
        onHide={cancelInvalidModal}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Resolve Invalid Product Types</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            The uploaded file contains productType values not present in the
            system. Please select the correct product type for each value.
          </p>
          {invalidTypes.map((inv) => (
            <div className="mb-2" key={inv}>
              <label className="form-label">{inv}</label>
              <select
                className="form-select"
                value={replacements[inv] || ""}
                onChange={(e) =>
                  setReplacements((prev) => ({
                    ...prev,
                    [inv]: e.target.value,
                  }))
                }
              >
                <option value="">Select product type</option>
                {productTypesList.map((pt) => (
                  <option key={pt.id} value={pt.productTypeId}>
                    {pt.productTypes}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={cancelInvalidModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={applyReplacements}>
            Apply Replacements
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default ImportWOProductsExcel;
